/*
  <aoa-hero-video>: decorative background video of the AoA home hero (sections/aoa-home-hero.liquid).
  The markup ships a <video> without a source (preload none) over the hero image, which stays the
  poster and the LCP element. This component only:
  - Gives the video its source once the hero image (LCP) has loaded, so it never competes with
    it and does not wait for third-party apps. data-src is used from 768px and data-src-mobile below (empty = keep the image).
  - Plays it muted and looped only without prefers-reduced-motion and without Save-Data. Otherwise
    the image stays and the button offers "Play video" (opt-in).
  - Pauses it while the hero is off screen (IntersectionObserver) and resumes it when it comes
    back, unless the visitor paused it.
  - Shows the pause / play button (WCAG 2.2.2). Its accessible name switches between
    data-label-pause and data-label-play (a state-changing label, so no aria-pressed as well).
  - Keeps the image if the video fails or autoplay is blocked.
  Listeners live on the element, the video, the button and two media queries, removed with an
  AbortController in disconnectedCallback (the theme editor re-renders the section).
*/
if (!customElements.get('aoa-hero-video')) {
  class AoaHeroVideo extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.video = this.querySelector('[data-aoa-hero-video]');
      this.button = this.querySelector('[data-aoa-hero-toggle]');
      if (!this.video) return;

      const controller = new AbortController();
      this.controller = controller;
      const signal = controller.signal;

      this.mobile = window.matchMedia('(max-width: 767px)');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.choice = null; // 'play' | 'pause' once the visitor used the button
      this.onScreen = false;
      this.failed = false;

      // iOS needs the property as well as the attribute to allow muted inline autoplay
      this.video.muted = true;
      this.video.defaultMuted = true;

      this.video.addEventListener('playing', () => this.render(), { signal });
      this.video.addEventListener('pause', () => this.render(), { signal });
      this.video.addEventListener('error', () => this.fail(), { signal });
      if (this.button) {
        this.button.addEventListener('click', () => this.toggle(), { signal });
      }
      this.mobile.addEventListener('change', () => this.update(), { signal });
      this.reducedMotion.addEventListener('change', () => this.update(), { signal });

      this.afterLoad(signal).then(() => {
        if (this.controller !== controller) return;
        this.observer = new IntersectionObserver((entries) => {
          this.onScreen = entries[entries.length - 1].isIntersecting;
          this.update();
        });
        this.observer.observe(this);
      });
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
      this.controller = null;
      if (this.observer) this.observer.disconnect();
      this.observer = null;
      if (this.video) this.video.pause();
    }

    // Resolves once the hero image (poster and LCP) has loaded, in the next frame, so the video
    // never competes with it but does not wait for the window load event either (third-party apps
    // delayed it 3-5 s). Falls back to the load event when there is no image.
    afterLoad(signal) {
      return new Promise((resolve) => {
        const start = () => requestAnimationFrame(() => resolve());
        const media = this.closest('.aoa-home-hero__media');
        const image = media && media.querySelector('.aoa-home-hero__image');
        if (image) {
          if (image.complete) {
            start();
          } else {
            image.addEventListener('load', start, { once: true, signal });
            image.addEventListener('error', start, { once: true, signal });
          }
        } else if (document.readyState === 'complete') {
          start();
        } else {
          window.addEventListener('load', start, { once: true, signal });
        }
      });
    }

    get source() {
      return (this.mobile.matches ? this.dataset.srcMobile : this.dataset.src) || '';
    }

    get autoplayAllowed() {
      const saveData = Boolean(navigator.connection && navigator.connection.saveData);
      return !this.reducedMotion.matches && !saveData;
    }

    update() {
      if (!this.observer) return;
      const source = this.source;
      if (!source || this.failed) {
        this.video.pause();
        this.removeAttribute('data-aoa-video-on');
        if (this.button) this.button.hidden = true;
        return;
      }
      if (this.button) this.button.hidden = false;

      const wanted = this.choice ? this.choice === 'play' : this.autoplayAllowed;
      if (wanted && this.onScreen) {
        this.play(source);
      } else if (!this.video.paused) {
        this.video.pause();
      }
      this.render();
    }

    play(source) {
      if (this.video.getAttribute('src') !== source) {
        this.removeAttribute('data-aoa-video-on');
        this.video.preload = 'auto';
        this.video.setAttribute('src', source);
      }
      const attempt = this.video.play();
      if (attempt && attempt.catch) {
        attempt.catch((error) => {
          // Blocked autoplay (low power mode, browser policy): keep the image, offer "Play video"
          if (error && error.name === 'NotAllowedError' && !this.choice) this.choice = 'pause';
          this.render();
        });
      }
    }

    toggle() {
      if (this.video.paused) {
        this.choice = 'play';
      } else {
        this.choice = 'pause';
      }
      this.update();
    }

    fail() {
      this.failed = true;
      this.update();
    }

    render() {
      const playing = !this.video.paused && !this.video.ended;
      if (playing) this.setAttribute('data-aoa-video-on', '');
      if (!this.button) return;
      this.button.dataset.state = playing ? 'playing' : 'paused';
      const label = playing ? this.button.dataset.labelPause : this.button.dataset.labelPlay;
      if (label) this.button.setAttribute('aria-label', label);
    }
  }

  customElements.define('aoa-hero-video', AoaHeroVideo);
}
